import type { TenantDto } from '@cartly/shared';
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Modal,
  SelectField,
  Spinner,
  TextField,
  useToast,
} from '@cartly/ui';
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { api } from '../../api/client';
import { formatDate } from '../../app/format';
import { useLoader, useSubmit } from '../../app/hooks';
import { useActiveSession, useSession } from '../../app/session';
import { COUNTRIES, CURRENCIES } from '../regions';

interface OrderSettings {
  autoCancelDeclinedCod: boolean;
  shopifyConnected: boolean;
  writeAccess: boolean;
}

function StoreWriteBack() {
  const { can } = useSession();
  const session = useActiveSession();
  const toast = useToast();
  const { data, error, setData } = useLoader(
    () => api.get<OrderSettings>('/order-settings'),
    session.activeTenant?.id,
  );
  const submit = useSubmit();
  if (error) return <Alert tone="critical">{error}</Alert>;
  if (!data || !data.shopifyConnected) return null;
  const editable = can('orders:manage');
  return (
    <Card title="Store updates" subtitle="What Cartly may change in your Shopify store on its own.">
      <div className="stack">
        <p className="text-sm">
          When a customer pays a payment link, Cartly marks the order as paid in Shopify so the
          courier does not collect again.
        </p>
        {!data.writeAccess && (
          <Alert tone="warning" title="Shopify permission needed">
            Approve the updated permissions for the Cartly360 app in your Shopify admin. Until then
            orders cannot be changed from Cartly.
          </Alert>
        )}
        <Checkbox
          label="Cancel the order in Shopify when a customer declines the COD confirmation"
          description="Restocks the items, no refund, the customer is not notified. Paid or fulfilled orders are never cancelled automatically."
          checked={data.autoCancelDeclinedCod}
          disabled={!editable || submit.pending}
          onChange={async (e) => {
            const value = e.target.checked;
            const updated = await submit.run(() =>
              api.put<OrderSettings>('/order-settings', { autoCancelDeclinedCod: value }),
            );
            if (updated) {
              setData(updated);
              toast.success('Saved');
            }
          }}
        />
      </div>
    </Card>
  );
}

export function OrganizationSettings() {
  const session = useActiveSession();
  const { data, error, loading, setData } = useLoader(
    () => api.get<TenantDto>('/tenants/current'),
    session.activeTenant?.id,
  );

  if (loading && !data) return <Spinner size="lg" label="Loading" />;
  if (error || !data) return <Alert tone="critical">{error ?? 'Failed to load'}</Alert>;
  // Keyed so the form re-initializes when another organization is loaded.
  return <OrganizationForm key={data.id} data={data} onSaved={setData} />;
}

function OrganizationForm({ data, onSaved }: { data: TenantDto; onSaved: (t: TenantDto) => void }) {
  const { can, refresh } = useSession();
  const session = useActiveSession();
  const toast = useToast();
  const editable = can('organization:manage');
  const [form, setForm] = useState({
    name: data.name,
    country: data.country,
    timezone: data.timezone,
    defaultCurrency: data.defaultCurrency,
  });
  const submit = useSubmit();

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const updated = await submit.run(() => api.patch<TenantDto>('/tenants/current', form));
    if (!updated) return;
    onSaved(updated);
    await refresh();
    toast.success('Organization updated');
  };

  const countryOptions = COUNTRIES.some((c) => c.value === form.country)
    ? COUNTRIES
    : [...COUNTRIES, { value: form.country, label: form.country }];
  const currencyOptions = CURRENCIES.some((c) => c.value === form.defaultCurrency)
    ? CURRENCIES
    : [...CURRENCIES, { value: form.defaultCurrency, label: form.defaultCurrency }];

  return (
    <div className="settings-grid">
      <Card title="Organization profile" subtitle="Used across the dashboard, emails and reports.">
        <form className="stack" onSubmit={onSubmit} noValidate>
          {submit.error && <Alert tone="critical">{submit.error}</Alert>}
          <TextField
            label="Business name"
            value={form.name}
            disabled={!editable}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            error={submit.fieldErrors['name']}
          />
          <div className="form-grid">
            <SelectField
              label="Country"
              value={form.country}
              options={countryOptions}
              disabled={!editable}
              onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))}
              error={submit.fieldErrors['country']}
            />
            <SelectField
              label="Default currency"
              value={form.defaultCurrency}
              options={currencyOptions}
              disabled={!editable}
              onChange={(e) => setForm((f) => ({ ...f, defaultCurrency: e.target.value }))}
              error={submit.fieldErrors['defaultCurrency']}
            />
            <TextField
              label="Time zone"
              fieldClassName="form-grid--full"
              value={form.timezone}
              disabled={!editable}
              onChange={(e) => setForm((f) => ({ ...f, timezone: e.target.value }))}
              error={submit.fieldErrors['timezone']}
            />
          </div>
          {editable && (
            <div className="form-actions">
              <Button type="submit" variant="primary" loading={submit.pending}>
                Save changes
              </Button>
            </div>
          )}
        </form>
      </Card>
      <StoreWriteBack />
      {session.activeTenant?.roleKey === 'owner' && <DangerZone org={data} />}
      <Card title="Details">
        <dl className="dl">
          <dt>Workspace URL</dt>
          <dd className="mono">{data.slug}</dd>
          <dt>Status</dt>
          <dd>
            <Badge tone={data.status === 'active' ? 'success' : 'warning'}>{data.status}</Badge>
          </dd>
          <dt>Pricing region</dt>
          <dd>{data.pricingRegion === 'india' ? 'India (INR)' : 'Global (USD)'}</dd>
          <dt>Created</dt>
          <dd>{formatDate(data.createdAt)}</dd>
        </dl>
      </Card>
    </div>
  );
}

function DangerZone({ org }: { org: TenantDto }) {
  const [open, setOpen] = useState(false);
  return (
    <Card
      title="Delete organization"
      subtitle="Permanently removes this organization and all of its data."
    >
      <div className="stack">
        <p className="text-muted">
          Stores, products, customers, orders, API keys, webhooks and team access for{' '}
          <strong>{org.name}</strong> are deleted immediately and cannot be recovered.
        </p>
        <div className="form-actions">
          <Button variant="danger" onClick={() => setOpen(true)}>
            Delete organization
          </Button>
        </div>
      </div>
      {open && <DeleteOrgModal org={org} onClose={() => setOpen(false)} />}
    </Card>
  );
}

function DeleteOrgModal({ org, onClose }: { org: TenantDto; onClose: () => void }) {
  const { refresh } = useSession();
  const toast = useToast();
  const navigate = useNavigate();
  const submit = useSubmit();
  const [confirmName, setConfirmName] = useState('');
  const [password, setPassword] = useState('');

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const ok = await submit.run(async () => {
      await api.delete('/tenants/current', { confirmName, password });
      return true;
    });
    if (!ok) return;
    toast.success(`${org.name} was deleted`);
    onClose();
    await refresh();
    navigate('/', { replace: true });
  };

  return (
    <Modal
      open
      title={`Delete ${org.name}?`}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="danger"
            type="submit"
            form="delete-org-form"
            loading={submit.pending}
            disabled={confirmName.trim() !== org.name || password === ''}
          >
            Delete forever
          </Button>
        </>
      }
    >
      <form id="delete-org-form" className="stack" onSubmit={onSubmit} noValidate>
        {submit.error && <Alert tone="critical">{submit.error}</Alert>}
        <Alert tone="warning" title="This cannot be undone">
          Everyone in this organization loses access. If a Shopify store is connected, the app stays
          installed in Shopify; opening it again creates a new, empty organization.
        </Alert>
        <TextField
          label={`Type "${org.name}" to confirm`}
          value={confirmName}
          onChange={(e) => setConfirmName(e.target.value)}
          error={submit.fieldErrors['confirmName']}
          autoComplete="off"
        />
        <TextField
          label="Your password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={submit.fieldErrors['password']}
          autoComplete="current-password"
        />
      </form>
    </Modal>
  );
}
